import { marked } from 'marked';
import hljs from 'highlight.js/lib/core';
import javascript from 'highlight.js/lib/languages/javascript';
import typescript from 'highlight.js/lib/languages/typescript';
import css from 'highlight.js/lib/languages/css';
import xml from 'highlight.js/lib/languages/xml';
import json from 'highlight.js/lib/languages/json';
import bash from 'highlight.js/lib/languages/bash';
import markdown from 'highlight.js/lib/languages/markdown';
import python from 'highlight.js/lib/languages/python';
import sql from 'highlight.js/lib/languages/sql';
import ini from 'highlight.js/lib/languages/ini';
import dockerfile from 'highlight.js/lib/languages/dockerfile';
import diff from 'highlight.js/lib/languages/diff';
import rust from 'highlight.js/lib/languages/rust';
import plaintext from 'highlight.js/lib/languages/plaintext';
import yaml from 'js-yaml';

hljs.registerLanguage('javascript', javascript);
hljs.registerLanguage('js', javascript);
hljs.registerLanguage('typescript', typescript);
hljs.registerLanguage('ts', typescript);
hljs.registerLanguage('css', css);
hljs.registerLanguage('html', xml);
hljs.registerLanguage('xml', xml);
hljs.registerLanguage('json', json);
hljs.registerLanguage('bash', bash);
hljs.registerLanguage('sh', bash);
hljs.registerLanguage('shell', bash);
hljs.registerLanguage('markdown', markdown);
hljs.registerLanguage('md', markdown);
hljs.registerLanguage('python', python);
hljs.registerLanguage('py', python);
hljs.registerLanguage('sql', sql);
hljs.registerLanguage('ini', ini);
hljs.registerLanguage('toml', ini);
hljs.registerLanguage('dockerfile', dockerfile);
hljs.registerLanguage('diff', diff);
hljs.registerLanguage('rust', rust);
hljs.registerLanguage('rs', rust);
hljs.registerLanguage('plaintext', plaintext);
hljs.registerLanguage('text', plaintext);

/** Escape text for safe interpolation into HTML attributes/content. */
function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

marked.use({
  renderer: {
    // 转义 Markdown 中的原生 HTML（块级 + 行内，如表格单元格里的 `<script setup>`）。
    // 否则裸 `<script>` 经 dangerouslySetInnerHTML 插入 DOM 后，浏览器会把它当成
    // 真正的开标签，吞掉其后所有内容直到 `</script>`（永远不会出现），导致文章渲染中断。
    html({ text }: { text: string }) {
      return escapeHtml(text);
    },
    code({ text, lang }: { text: string; lang?: string }) {
      // 规范化 info string（如 "typescript{1,3}" → "typescript"）
      const cleanLang = (lang || '').trim().split(/[\s{]/)[0].toLowerCase();
      const validLang = cleanLang && hljs.getLanguage(cleanLang);
      const highlighted = validLang
        ? hljs.highlight(text, { language: cleanLang }).value
        : hljs.highlightAuto(text).value;
      const langLabel = escapeHtml(validLang ? cleanLang.toUpperCase() : 'CODE');
      const langClass = escapeHtml(cleanLang || 'plaintext');
      // 独立小框：代码块卡片 = header(语言标签 + copy 按钮) + 高亮 pre
      return (
        '<div class="code-block">' +
        '<div class="code-block__header">' +
        `<span class="code-block__lang">${langLabel}</span>` +
        '<button class="code-block__copy" type="button" aria-label="Copy code">COPY</button>' +
        '</div>' +
        `<pre><code class="hljs language-${langClass}">${highlighted}</code></pre>` +
        '</div>'
      );
    },
  },
});

/**
 * Resolve relative image/src paths in rendered HTML.
 * - "assets/photo.jpg" → "/api/files/{docSlug}/assets/photo.jpg"
 * - "/raw/ai-docs/xxx/assets/SKILL.md" → "/api/files/ai-docs/xxx/assets/SKILL.md"
 */
export function resolveHtmlAssetPaths(html: string, docSlug: string): string {
  if (!docSlug) return html;
  let result = html;
  // 1. Convert /raw/ paths to /api/files/
  result = result.replace(
    /\/(raw)\/([^"\s]+)/g,
    '/api/files/$2'
  );
  // 2. Convert relative asset paths to /api/files/{docSlug}/...
  result = result.replace(
    /(src|href)="(assets\/[^"]+)"/g,
    `$1="/api/files/${docSlug}/$2"`
  );
  return result;
}

function parseFrontmatter(content: string): { data: Record<string, unknown>; body: string } {
  // Normalize \r\n → \n
  const normalized = content.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const match = normalized.match(/^---\n([\s\S]*?)\n---\n([\s\S]*)$/);
  if (!match) return { data: {}, body: content };

  const yamlStr = match[1];
  const body = match[2];

  let data: Record<string, unknown> = {};
  try {
    data = yaml.load(yamlStr) as Record<string, unknown>;
    if (!data) data = {};
  } catch {
    data = {};
  }

  return { data, body };
}

export function parseMarkdown(content: string): { data: Record<string, unknown>; content: string } {
  const { data, body } = parseFrontmatter(content);
  const html = marked(body) as string;
  return { data, content: html };
}

export function parseFrontmatterData(content: string): Record<string, unknown> {
  return parseFrontmatter(content).data;
}

/**
 * Attach copy-button handlers to rendered code blocks inside a container.
 * The raw code text is read from the adjacent `<pre><code>` DOM node so we
 * avoid re-encoding/escaping issues from embedding code in HTML attributes.
 * Returns a cleanup function that detaches the listeners (for React unmount).
 */
export function attachCodeBlockCopy(container: HTMLElement | null): (() => void) | undefined {
  if (!container) return undefined;
  const buttons = Array.from(container.querySelectorAll<HTMLButtonElement>('.code-block__copy'));
  const cleanup: Array<() => void> = [];
  buttons.forEach((btn) => {
    const codeEl = btn.closest('.code-block')?.querySelector<HTMLElement>('pre code');
    const onCopy = () => {
      void copyText(codeEl?.textContent ?? '').then(() => {
        const original = btn.textContent;
        btn.textContent = 'COPIED';
        btn.classList.add('is-copied');
        window.setTimeout(() => {
          btn.textContent = original;
          btn.classList.remove('is-copied');
        }, 2000);
      });
    };
    btn.addEventListener('click', onCopy);
    cleanup.push(() => btn.removeEventListener('click', onCopy));
  });
  return () => cleanup.forEach((detach) => detach());
}

/** Copy text to clipboard, with a fallback for non-secure contexts. */
async function copyText(text: string): Promise<void> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }
    throw new Error('Clipboard API unavailable');
  } catch {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
  }
}
